
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { apiFetch } from '../utils/apiAuth';
import { useDraggable } from '../hooks/useDraggable';

/**
 * Lightweight Markdown renderer for assistant messages.
 * Formats headers, bold/italic, lists, blockquotes, inline code, and code blocks.
 * Automatically detects and links order IDs to /admin/afas-transactions/:id
 * and customer domains/names to /admin/customers/:id.
 */
const FormattedMessage = ({ text, customerLinks = {} }) => {
  if (!text) return null;

  const parts = text.split(/(```[\s\S]*?```)/g);

  return (
    <div className="space-y-2 text-sm leading-relaxed break-words">
      {parts.map((part, pIdx) => {
        if (part.startsWith('```') && part.endsWith('```')) {
          const lines = part.slice(3, -3).trim().split('\n');
          const firstLine = lines[0].trim();
          const hasLang = firstLine && !firstLine.includes(' ') && lines.length > 1;
          const lang = hasLang ? firstLine : '';
          const codeContent = hasLang ? lines.slice(1).join('\n') : lines.join('\n');

          return (
            <div key={pIdx} className="my-2 rounded-lg bg-gray-900 text-gray-100 overflow-hidden shadow-sm">
              {lang && (
                <div className="px-3 py-1 bg-gray-800 text-[11px] text-gray-400 font-mono border-b border-gray-700">
                  {lang}
                </div>
              )}
              <pre className="p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap selection:bg-purple-800">
                {codeContent}
              </pre>
            </div>
          );
        }

        const rawLines = part.split('\n');
        const blocks = [];
        let currentTable = null;

        for (let i = 0; i < rawLines.length; i++) {
          const l = rawLines[i];
          const trimmed = l.trim();
          if (trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.length > 2) {
            if (!currentTable) {
              currentTable = [];
            }
            currentTable.push(trimmed);
          } else {
            if (currentTable) {
              blocks.push({ type: 'table', lines: currentTable });
              currentTable = null;
            }
            blocks.push({ type: 'line', text: l });
          }
        }
        if (currentTable) {
          blocks.push({ type: 'table', lines: currentTable });
        }

        return (
          <div key={pIdx} className="space-y-1">
            {blocks.map((block, bIdx) => {
              if (block.type === 'table') {
                const tableLines = block.lines;
                if (tableLines.length >= 2) {
                  const parseRow = (str) =>
                    str
                      .split('|')
                      .slice(1, -1)
                      .map((c) => c.trim());
                  const headers = parseRow(tableLines[0]);
                  const dataRows = tableLines.slice(2).map(parseRow);

                  return (
                    <div key={bIdx} className="my-2.5 overflow-x-auto rounded-lg border border-gray-200/80 shadow-xs bg-white/75 backdrop-blur-xs">
                      <table className="min-w-full text-xs text-left divide-y divide-gray-200/60">
                        <thead className="bg-fuchsia-50/90 text-primary font-bold">
                          <tr>
                            {headers.map((h, hIdx) => (
                              <th key={hIdx} className="px-2.5 py-1.5 whitespace-nowrap">
                                {parseInline(h, customerLinks)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100/60 bg-white/60">
                          {dataRows.map((r, rIdx) => (
                            <tr key={rIdx} className="hover:bg-fuchsia-50/40 transition">
                              {r.map((cell, cIdx) => (
                                <td key={cIdx} className="px-2.5 py-1.5 whitespace-nowrap text-gray-700">
                                  {parseInline(cell, customerLinks)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                }
              }

              const line = block.text;
              const trimmed = line.trim();
              if (!trimmed) return <div key={bIdx} className="h-1" />;

              if (trimmed.startsWith('### ')) {
                return (
                  <h4 key={bIdx} className="font-bold text-gray-900 text-sm mt-2 mb-1">
                    {parseInline(trimmed.replace(/^###\s+/, ''), customerLinks)}
                  </h4>
                );
              }
              if (trimmed.startsWith('## ') || trimmed.startsWith('# ')) {
                return (
                  <h3 key={bIdx} className="font-bold text-gray-900 text-base mt-2.5 mb-1 pb-0.5 border-b border-gray-200">
                    {parseInline(trimmed.replace(/^#+\s+/, ''), customerLinks)}
                  </h3>
                );
              }
              if (/^[-*]\s+/.test(trimmed)) {
                return (
                  <div key={bIdx} className="flex items-start gap-2 pl-2">
                    <span className="text-primary font-bold mt-0.5">•</span>
                    <span className="flex-1 text-gray-800">{parseInline(trimmed.replace(/^[-*]\s+/, ''), customerLinks)}</span>
                  </div>
                );
              }
              if (/^\d+\.\s+/.test(trimmed)) {
                const numMatch = trimmed.match(/^(\d+)\.\s+(.*)/);
                return (
                  <div key={bIdx} className="flex items-start gap-2 pl-2">
                    <span className="font-semibold text-primary text-xs mt-0.5">{numMatch[1]}.</span>
                    <span className="flex-1 text-gray-800">{parseInline(numMatch[2], customerLinks)}</span>
                  </div>
                );
              }

              return (
                <p key={bIdx} className="text-gray-800">
                  {parseInline(line, customerLinks)}
                </p>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

const parseInline = (text, customerLinks = {}) => {
  if (!text) return '';
  const tokens = text.split(/(\*\*.*?\*\*|`.*?`|\[.*?\]\(.*?\))/g);

  return tokens.map((token, idx) => {
    if (token.startsWith('**') && token.endsWith('**')) {
      return (
        <strong key={idx} className="font-semibold text-gray-900">
          {parseInline(token.slice(2, -2), customerLinks)}
        </strong>
      );
    }
    if (token.startsWith('`') && token.endsWith('`')) {
      const codeVal = token.slice(1, -1);
      // Order ID / voucher number (e.g. `XW26G00495` or `XB26G00136`)
      if (/^(?:X[BW]|SKP)\d{2}[A-Za-z0-9]+$/i.test(codeVal)) {
        return (
          <a
            key={idx}
            href={`/admin/afas-transactions/${codeVal}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-1.5 py-0.5 rounded bg-fuchsia-50 text-primary hover:bg-fuchsia-100 hover:text-fuchsia-900 hover:underline font-mono text-xs border border-fuchsia-200 inline-flex items-center gap-0.5 transition"
            title={`Open AFAS Transaction ${codeVal}`}
          >
            {codeVal}
            <svg className="w-2.5 h-2.5 inline-block opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        );
      }
      // Customer ID (e.g. `C04k4hxr0`)
      if (/^C0[a-z0-9]{7,10}$/i.test(codeVal)) {
        return (
          <a
            key={idx}
            href={`/admin/customers/${codeVal}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-1.5 py-0.5 rounded bg-sky-50 text-primary hover:bg-sky-100 hover:text-fuchsia-900 hover:underline font-mono text-xs border border-sky-200 inline-flex items-center gap-0.5 transition"
            title={`Open Customer ${codeVal}`}
          >
            {codeVal}
            <svg className="w-2.5 h-2.5 inline-block opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        );
      }
      return (
        <code key={idx} className="px-1.5 py-0.5 rounded bg-gray-100 text-primary font-mono text-xs border border-gray-200">
          {codeVal}
        </code>
      );
    }
    const linkMatch = token.match(/^\[(.*?)\]\((.*?)\)$/);
    if (linkMatch) {
      return (
        <a
          key={idx}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-fuchsia-900 hover:underline font-semibold inline-flex items-center gap-0.5 transition"
          title={`Open ${linkMatch[1]}`}
        >
          {linkMatch[1]}
          <svg className="w-3 h-3 inline-block opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      );
    }

    // Auto-detect voucher numbers / order IDs in plain text (e.g. XB26G00136, XW26G00495),
    // customer IDs (e.g. C04k4hxr0), and known customer domains/names from customerLinks
    const linkKeys = Object.keys(customerLinks || {}).filter((k) => k && k.length > 2);
    linkKeys.sort((a, b) => b.length - a.length);

    const escapeRegex = (s) => s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

    const patterns = [
      '\\b(?:X[BW]|SKP)\\d{2}[A-Za-z0-9]+\\b',
      '\\bC0[a-z0-9]{7,10}\\b',
    ];
    if (linkKeys.length > 0) {
      patterns.push(linkKeys.map((k) => `\\b${escapeRegex(k)}\\b`).join('|'));
    }

    const combinedRegex = new RegExp(`(${patterns.join('|')})`, 'gi');
    if (!combinedRegex.test(token)) {
      return token;
    }

    const parts = token.split(combinedRegex);
    return parts.map((part, pIdx) => {
      if (!part) return null;

      // Voucher match
      if (/^(?:X[BW]|SKP)\d{2}[A-Za-z0-9]+$/i.test(part)) {
        return (
          <a
            key={`${idx}-${pIdx}`}
            href={`/admin/afas-transactions/${part}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-fuchsia-900 hover:underline font-semibold font-mono inline-flex items-center gap-0.5 transition"
            title={`Open AFAS Transaction ${part}`}
          >
            {part}
            <svg className="w-2.5 h-2.5 inline-block opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        );
      }

      // Customer ID match (e.g. C04k4hxr0)
      if (/^C0[a-z0-9]{7,10}$/i.test(part)) {
        return (
          <a
            key={`${idx}-${pIdx}`}
            href={`/admin/customers/${part}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-fuchsia-900 hover:underline font-semibold font-mono inline-flex items-center gap-0.5 transition"
            title={`Open Customer ${part}`}
          >
            {part}
            <svg className="w-2.5 h-2.5 inline-block opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        );
      }

      // Customer Domain or Company Name match from customerLinks
      const matchedCid = customerLinks[part] || customerLinks[part.toLowerCase()];
      if (matchedCid) {
        return (
          <a
            key={`${idx}-${pIdx}`}
            href={`/admin/customers/${matchedCid}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-fuchsia-900 hover:underline font-semibold inline-flex items-center gap-0.5 transition"
            title={`Open Customer ${part}`}
          >
            {part}
            <svg className="w-2.5 h-2.5 inline-block opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        );
      }

      return part;
    });
  });
};

const SUGGESTED_QUESTIONS = [
  { label: 'Reminder emails sent yesterday?', prompt: 'How many subscription renewal reminder emails were sent yesterday, and to which customers?' },
  { label: 'GCP billing this month?', prompt: 'How many customers will be billed for GCP for September 2026, and what is the total amount?' },
  { label: 'Expiring licenses this week?', prompt: 'Which customer Google Subscriptions licenses will expire in this week?' },
  { label: 'Invoices created last month', prompt: 'Please show me the invoices created last month, Sept.' },
  { label: 'Why did customer not receive PDF invoice?', prompt: 'Why did customer not receive PDF invoice for this order? (Please check Peppol, PDF link, and billing emails)' },
  { label: 'Is this customer paid? Why failed?', prompt: 'Is this customer paid? If not, why did the payment fail and what is the error reason?' },
  { label: 'Diagnose recent sync or job errors', prompt: 'Check recent job logs and tell me if there are any failures in AFAS sync or renewals.' },
];

const DEFAULT_ASSISTANT_GREETING = "👋 Hi! I'm the **Phoenix Admin AI Assistant**.\nI have real-time, read-only access to Firestore customers, orders, GCP reseller billing, Peppol e-invoicing, and job logs. Ask me any diagnostic question!";

const AdminAIAssistantDrawer = ({ isOpen, onClose, currentContext = {} }) => {
  const [messages, setMessages] = useState([
    {
      role: 'model',
      text: "👋 Hi! I'm the **Phoenix Admin AI Assistant**.\n*Fetching today's activities summary...*",
    },
  ]);
  const initialGreetingRef = useRef(null);
  const [greetingFetched, setGreetingFetched] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedTools, setExpandedTools] = useState({});
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  // Accumulated map of customer domains & company names to customer IDs across all conversation messages
  const allCustomerLinks = useMemo(() => {
    const map = {};
    messages.forEach((m) => {
      if (m.customerLinks) {
        Object.assign(map, m.customerLinks);
      }
    });
    return map;
  }, [messages]);

  // Position state (Picture-in-Picture floating)
  const { position, isDragging, handlers: dragHandlers } = useDraggable(() => {
    if (typeof window !== 'undefined') {
      const defaultW = 460;
      const defaultH = Math.min(650, window.innerHeight * 0.8);
      // Place nicely on the right or middle-right by default
      return {
        x: Math.max(30, window.innerWidth - defaultW - 40),
        y: Math.max(30, window.innerHeight - defaultH - 40),
      };
    }
    return { x: 80, y: 80 };
  }, {
    disabled: isMaximized,
    clamp: ({ x, y }) => ({
      x: Math.max(10, Math.min(window.innerWidth - 100, x)),
      y: Math.max(10, Math.min(window.innerHeight - 50, y)),
    }),
  });

  // Size state (resizable)
  const [size, setSize] = useState(() => ({
    width: 460,
    height: typeof window !== 'undefined' ? Math.min(650, window.innerHeight * 0.8) : 600,
  }));

  // Resizing state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, w: 0, h: 0 });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const pipRef = useRef(null);

  const [selectedImages, setSelectedImages] = useState([]); // Array of { id, dataUrl, mimeType, base64, name }

  const apiUrl = process.env.REACT_APP_API_URL || '';
  const MAX_IMAGES = 5;

  const processImageFiles = (files) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);

    setSelectedImages((prev) => {
      const availableSlots = MAX_IMAGES - prev.length;
      if (availableSlots <= 0) {
        toast.error(`Maximum ${MAX_IMAGES} images allowed.`);
        return prev;
      }

      const filesToProcess = fileList.slice(0, availableSlots);
      if (fileList.length > availableSlots) {
        toast(`Only ${availableSlots} more image(s) could be added (max ${MAX_IMAGES}).`, { icon: 'ℹ️' });
      }

      filesToProcess.forEach((file) => {
        if (!file || !file.type.startsWith('image/')) {
          toast.error(`${file.name || 'File'} is not an image (PNG, JPG, WebP).`);
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name || 'Image'} exceeds the 10MB limit.`);
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target.result;
          const base64 = dataUrl.split(',')[1] || '';
          setSelectedImages((current) => {
            if (current.length >= MAX_IMAGES) return current;
            return [
              ...current,
              {
                id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                dataUrl,
                mimeType: file.type || 'image/png',
                base64,
                name: file.name || 'Screenshot',
              },
            ];
          });
        };
        reader.readAsDataURL(file);
      });

      return prev;
    });
  };

  const removeImage = (id) => {
    setSelectedImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pastedImageFiles = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type && items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) pastedImageFiles.push(file);
      }
    }
    if (pastedImageFiles.length > 0) {
      e.preventDefault();
      processImageFiles(pastedImageFiles);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (greetingFetched) return;
    let isMounted = true;

    const fetchGreeting = async () => {
      try {
        const response = await apiFetch(`${apiUrl}/api/admin/assistant/greeting`);
        if (response.ok) {
          const data = await response.json();
          if (isMounted && data?.greeting) {
            const greetingMsg = {
              role: 'model',
              text: data.greeting,
              customerLinks: data.customer_links || {},
            };
            initialGreetingRef.current = greetingMsg;
            setMessages((prev) => {
              if (prev.length <= 1 && (prev.length === 0 || prev[0].role === 'model')) {
                return [greetingMsg];
              }
              return prev;
            });
            setGreetingFetched(true);
            return;
          }
        }
      } catch (err) {
        console.warn('Failed to load dynamic greeting:', err);
      }

      if (isMounted) {
        const fallbackMsg = {
          role: 'model',
          text: DEFAULT_ASSISTANT_GREETING,
        };
        initialGreetingRef.current = fallbackMsg;
        setMessages((prev) => {
          if (prev.length <= 1 && (prev.length === 0 || prev[0].role === 'model')) {
            return [fallbackMsg];
          }
          return prev;
        });
        setGreetingFetched(true);
      }
    };

    fetchGreeting();

    return () => {
      isMounted = false;
    };
  }, [apiUrl, greetingFetched]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      scrollToBottom();
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen, messages, isMinimized]);

  // Resize handler (bottom-right corner)
  const handleResizePointerDown = (e) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      w: size.width,
      h: size.height,
    });
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleResizePointerMove = (e) => {
    if (isResizing) {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      const newW = Math.max(340, Math.min(window.innerWidth - position.x - 20, resizeStart.w + deltaX));
      const newH = Math.max(380, Math.min(window.innerHeight - position.y - 20, resizeStart.h + deltaY));
      setSize({ width: newW, height: newH });
    }
  };

  const handleResizePointerUp = (e) => {
    if (isResizing) {
      setIsResizing(false);
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (_) { }
    }
  };

  const handleSendMessage = async (textToSend) => {
    const prompt = (textToSend || input).trim();
    if ((!prompt && selectedImages.length === 0) || loading) return;

    const currentImagesToSend = [...selectedImages];
    setInput('');
    setSelectedImages([]);
    if (fileInputRef.current) fileInputRef.current.value = '';

    const defaultPrompt = currentImagesToSend.length > 1
      ? 'Please analyze these images and find the relevant customer, order, or information shown.'
      : (currentImagesToSend.length === 1
        ? 'Please analyze this image and find the relevant customer, order, or information shown.'
        : '');

    const newMessages = [
      ...messages,
      {
        role: 'user',
        text: prompt || defaultPrompt,
        images: currentImagesToSend.map((img) => img.dataUrl),
        image: currentImagesToSend.length > 0 ? currentImagesToSend[0].dataUrl : null,
      },
    ];
    setMessages(newMessages);
    setLoading(true);

    try {
      const response = await apiFetch(`${apiUrl}/api/admin/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: prompt || defaultPrompt,
          images: currentImagesToSend.map((img) => ({
            mimeType: img.mimeType,
            data: img.base64,
            name: img.name,
          })),
          image: currentImagesToSend.length > 0 ? {
            mimeType: currentImagesToSend[0].mimeType,
            data: currentImagesToSend[0].base64,
          } : null,
          history: newMessages.map((m) => ({
            role: m.role,
            content: m.text,
          })),
          context: currentContext,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errMsg = data.message || data.error || 'Failed to communicate with AI Assistant.';
        toast.error(errMsg);
        setMessages((prev) => [
          ...prev,
          {
            role: 'model',
            text: `⚠️ **Error**: ${errMsg}`,
            isError: true,
          },
        ]);
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'model',
          text: data.reply || 'No response returned.',
          toolsUsed: data.tools_used || [],
          customerLinks: data.customer_links || {},
        },
      ]);
    } catch (err) {
      console.error('AI Assistant chat error:', err);
      toast.error('Network error contacting AI Assistant.');
      setMessages((prev) => [
        ...prev,
        {
          role: 'model',
          text: '⚠️ **Network error**: Could not reach the AI assistant server.',
          isError: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      initialGreetingRef.current || {
        role: 'model',
        text: DEFAULT_ASSISTANT_GREETING,
      },
    ]);
  };

  if (!isOpen) return null;

  // Window styling: normal PiP vs maximized vs minimized
  const containerStyle = isMaximized
    ? {
      position: 'fixed',
      top: '16px',
      left: '16px',
      width: 'calc(100vw - 32px)',
      height: 'calc(100vh - 32px)',
      zIndex: 100,
    }
    : isMinimized
      ? {
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: '48px',
        zIndex: 100,
      }
      : {
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        zIndex: 100,
      };

  return (
    <div
      ref={pipRef}
      style={containerStyle}
      className={`bg-white/80 backdrop-blur-xl rounded-xl shadow-2xl flex flex-col overflow-hidden transition-all duration-200 ${loading ? 'ai-shimmer-border' : 'border border-white/60 ring-1 ring-black/10'
        } ${isDragging ? 'opacity-90 shadow-2xl scale-[1.01]' : ''}`}
    >
      {/* Draggable PiP Header */}
      <div
        className="px-4 py-3 bg-gradient-to-r from-primary/95 to-[#500051]/95 backdrop-blur-md text-white flex items-center justify-between cursor-move select-none shadow-md shrink-0"
        {...dragHandlers}
      >
        <div className="flex items-center gap-2 pointer-events-none">
          <div className="w-7 h-7 rounded-full bg-white/20 p-0.5 flex items-center justify-center shrink-0 border border-white/30 shadow-sm">
            <img
              src="/phoenix-bot.png"
              alt="Phoenix AI Assistant"
              className="w-full h-full object-contain filter drop-shadow-sm select-none"
            />
          </div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-xs tracking-wide">Phoenix AI Assistant</h3>
            </div>
        </div>

        {/* Window controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={clearChat}
            title="Clear chat"
            className="p-1 hover:bg-white/10 rounded transition text-fuchsia-100 hover:text-white"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>

          {/* Minimize / Expand */}
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            title={isMinimized ? 'Expand' : 'Minimize'}
            className="p-1 hover:bg-white/10 rounded transition text-fuchsia-100 hover:text-white"
          >
            {isMinimized ? (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </button>

          {/* Maximize / Restore */}
          <button
            onClick={() => {
              setIsMaximized(!isMaximized);
              setIsMinimized(false);
            }}
            title={isMaximized ? 'Restore size' : 'Maximize'}
            className="p-1 hover:bg-white/10 rounded transition text-fuchsia-100 hover:text-white"
          >
            {isMaximized ? (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 14h6m0 0v6m0-6L3 21m17-7h-6m0 0v6m0-6l7 7M10 10V4m0 6H4m6 0L3 3m10 7h6m0 0V4m0 6l7-7" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            )}
          </button>

          {/* Close */}
          <button
            onClick={onClose}
            title="Close"
            className="p-1 hover:bg-white/10 rounded transition text-fuchsia-100 hover:text-white"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Current Screen Context Tag if present */}
          {currentContext && (currentContext.currentDomain || currentContext.orderId) && (
            <div className="px-3 py-1 bg-fuchsia-50/75 backdrop-blur-xs border-b border-fuchsia-100/60 text-[11px] text-primary flex items-center gap-1.5 shrink-0">
              <span className="font-semibold">Context:</span>
              {currentContext.currentDomain && <span className="bg-white/80 px-1.5 py-0.5 rounded border border-fuchsia-200">{currentContext.currentDomain}</span>}
              {currentContext.orderId && <span className="bg-white/80 px-1.5 py-0.5 rounded border border-fuchsia-200">{currentContext.orderId}</span>}
            </div>
          )}

          {/* Message Stream */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white/45 backdrop-blur-md">
            {messages.map((m, idx) => {
              const isUser = m.role === 'user';
              return (
                <div key={idx} className={`flex flex-col ${isUser ? 'items-end' : 'items-start w-full'}`}>
                  {isUser ? (
                    <div className="max-w-[85%] rounded-2xl rounded-br-sm px-3.5 py-2 bg-primary text-white shadow-sm text-sm whitespace-pre-wrap">
                      {/* Attached Image(s) */}
                      {(m.images?.length > 0 || m.image) && (
                        <div
                          className={`mb-2 grid gap-1.5 ${
                            (m.images?.length || 1) === 1
                              ? 'grid-cols-1'
                              : (m.images?.length || 1) === 2
                              ? 'grid-cols-2'
                              : 'grid-cols-3'
                          }`}
                        >
                          {(m.images || [m.image]).map((imgSrc, imgIdx) => (
                            <img
                              key={imgIdx}
                              src={imgSrc}
                              alt={`Attachment ${imgIdx + 1}`}
                              className="w-full max-h-40 rounded-lg border border-white/20 object-cover bg-black/20 cursor-pointer hover:opacity-90 transition shadow-xs"
                              onClick={() => window.open(imgSrc, '_blank')}
                              title="Click to view full size"
                            />
                          ))}
                        </div>
                      )}
                      {m.text}
                    </div>
                  ) : (
                    <div
                      className={`w-full text-gray-900 ${
                        m.isError
                          ? 'rounded-lg p-3 bg-red-50 text-red-800 border border-red-200'
                          : 'text-sm leading-relaxed'
                      }`}
                    >
                      <FormattedMessage text={m.text} customerLinks={allCustomerLinks} />
                    </div>
                  )}

                  {/* Tools Used Inspector */}
                  {m.toolsUsed && m.toolsUsed.length > 0 && (
                    <div className="mt-1 pl-0.5">
                      <button
                        onClick={() => setExpandedTools((prev) => ({ ...prev, [idx]: !prev[idx] }))}
                        className="inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-primary transition"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        <span>{m.toolsUsed.length} Firestore tool{m.toolsUsed.length > 1 ? 's' : ''} called</span>
                        <svg
                          className={`w-3 h-3 transition-transform ${expandedTools[idx] ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {expandedTools[idx] && (
                        <div className="mt-1.5 p-2 bg-gray-50 rounded-lg border border-gray-200 text-[10px] font-mono text-gray-700 space-y-1">
                          {m.toolsUsed.map((t, tIdx) => (
                            <div key={tIdx} className="flex items-center gap-1">
                              <span className="text-primary font-bold">{t.tool}</span>
                              <span className="text-gray-400">({JSON.stringify(t.args)})</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {loading && (
              <div className="flex items-center gap-2 py-1 text-xs text-gray-500 pl-0.5">
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
                <span className="text-xs text-gray-500 ml-1">Analyzing Firestore & APIs...</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Suggestion Chips */}
          <div className="p-2 bg-white/60 backdrop-blur-md border-t border-gray-200/60 shrink-0">
            <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
              {SUGGESTED_QUESTIONS.map((q, qIdx) => (
                <button
                  key={qIdx}
                  onClick={() => handleSendMessage(q.prompt)}
                  disabled={loading}
                  className="whitespace-nowrap px-2 py-0.5 bg-white/85 hover:bg-white hover:text-primary hover:border-primary text-gray-700 text-[11px] rounded-full border border-gray-200/80 shadow-2xs transition disabled:opacity-50"
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Images Preview Strip */}
          {selectedImages.length > 0 && (
            <div className="px-3 pt-2 pb-2 bg-white/80 backdrop-blur-md border-t border-gray-100/70">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-medium text-fuchsia-700 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500 animate-pulse" />
                  {selectedImages.length} image{selectedImages.length > 1 ? 's' : ''} attached (max 5)
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedImages([]);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="text-[10px] text-gray-400 hover:text-red-500 transition underline"
                >
                  Clear all
                </button>
              </div>
              <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin">
                {selectedImages.map((img) => (
                  <div key={img.id} className="relative group shrink-0">
                    <img
                      src={img.dataUrl}
                      alt={img.name}
                      className="w-12 h-12 object-cover rounded-lg border border-fuchsia-200 shadow-xs group-hover:brightness-95 transition"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(img.id)}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-gray-800 text-white flex items-center justify-center text-[10px] hover:bg-red-500 shadow-xs transition"
                      title="Remove image"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Chat Input */}
          <div className="p-2.5 bg-white/75 backdrop-blur-md border-t border-gray-200/60 flex items-center gap-2 shrink-0">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  processImageFiles(e.target.files);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || selectedImages.length >= 5}
              title={
                selectedImages.length >= 5
                  ? 'Maximum 5 images attached'
                  : 'Attach images or screenshots (select multiple or paste with Cmd+V)'
              }
              className="p-2 text-gray-500 hover:text-primary hover:bg-fuchsia-50 rounded-lg transition disabled:opacity-40"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={
                selectedImages.length > 0
                  ? `Add a question or press Enter to analyze ${selectedImages.length} image${selectedImages.length > 1 ? 's' : ''}...`
                  : 'Ask questions or paste/upload screenshots...'
              }
              disabled={loading}
              className="flex-1 resize-none max-h-20 p-2 text-xs bg-white/80 border border-gray-300/80 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent focus:bg-white placeholder-gray-400 transition-colors"
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={(!input.trim() && selectedImages.length === 0) || loading}
              className="p-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-40 disabled:hover:bg-primary transition shadow flex items-center justify-center"
              title="Send Message"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>

          {/* Resizing Handle (Bottom-Right Corner) */}
          {!isMaximized && (
            <div
              className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize flex items-end justify-end p-0.5 text-gray-400 hover:text-primary transition"
              onPointerDown={handleResizePointerDown}
              onPointerMove={handleResizePointerMove}
              onPointerUp={handleResizePointerUp}
              title="Drag to resize"
            >
              <svg className="w-3 h-3" viewBox="0 0 16 16" fill="currentColor">
                <path d="M14 14H12V12H14V14ZM14 10H12V8H14V10ZM10 14H8V12H10V14ZM14 6H12V4H14V6ZM6 14H4V12H6V14ZM10 10H8V8H10V10Z" />
              </svg>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminAIAssistantDrawer;
